const GUEST_CART_STORAGE_KEY = "tl_guest_cart";

/**
 * Loads the guest cart from localStorage.
 * Fallbacks gracefully to empty array if storage is empty or corrupt.
 */
export function loadGuestCart() {
  try {
    const raw = localStorage.getItem(GUEST_CART_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    console.warn("Could not load guest cart from localStorage:", err);
    return [];
  }
}

/**
 * Saves the guest cart array to localStorage.
 */
export function saveGuestCart(cart) {
  try {
    if (!Array.isArray(cart)) return;
    localStorage.setItem(GUEST_CART_STORAGE_KEY, JSON.stringify(cart));
  } catch (err) {
    console.warn("Could not save guest cart to localStorage:", err);
  }
}

/**
 * Clears the guest cart from localStorage.
 */
export function clearGuestCartStorage() {
  try {
    localStorage.removeItem(GUEST_CART_STORAGE_KEY);
  } catch (err) {
    console.warn("Could not clear guest cart storage:", err);
  }
}

/**
 * Generates a unique stable identity key for a variant combination in the cart.
 */
export function createCartItemId(productId, sizeVariantId, colorVariantId) {
  return `${productId || "prod"}_${sizeVariantId || "nosize"}_${colorVariantId || "nocolor"}`;
}

/**
 * Normalizes an item for storage in the guest cart.
 * Ensures compatibility with backend GuestAddItemToCartDto while storing
 * essential snapshot metadata needed for offline frontend rendering.
 */
export function normalizeGuestCartItem(item) {
  const quantity = Math.max(1, Number(item.quantity ?? item.qty ?? 1));
  const productId = item.productId || item.id || "";
  const sizeVariantId = item.sizeVariantId || "";
  const colorVariantId = item.colorVariantId || "";

  return {
    id: item.id || createCartItemId(productId, sizeVariantId, colorVariantId),
    productId,
    // Backend GuestAddItemToCartDto fields:
    quantity,
    sizeVariantId,
    colorVariantId,
    // Frontend snapshot fields for zero-backend calculation:
    name: item.name || "Product",
    arabicName: item.arabicName || "",
    price: Number(item.price ?? item.basePrice ?? 0),
    length: Number(item.length ?? item.product?.length ?? 0),
    width: Number(item.width ?? item.product?.width ?? 0),
    height: Number(item.height ?? item.product?.height ?? 0),
    sizeName: item.sizeName || item.size || "",
    colorName: item.colorName || item.color || "",
    colorHex: item.colorHex || "",
    imageUrl: item.imageUrl || item.url || "",
    displayOrder: typeof item.displayOrder === "number" ? item.displayOrder : undefined,
    categoryName: item.categoryName || "",
  };
}

/**
 * Extracts pure GuestAddItemToCartDto objects for future backend checkout submission.
 * Backend C# model:
 * public class GuestAddItemToCartDto {
 *     public int Quantity { get; set; }
 *     public Guid SizeVariantId { get; set; }
 *     public Guid ColorVariantId { get; set; }
 * }
 */
export function toGuestAddItemToCartDto(item) {
  return {
    quantity: Number(item.quantity) || 1,
    sizeVariantId: item.sizeVariantId,
    colorVariantId: item.colorVariantId,
  };
}

/**
 * Maps the entire guest cart to an array of GuestAddItemToCartDto objects.
 */
export function toGuestCheckoutItems(guestCart) {
  if (!Array.isArray(guestCart)) return [];
  return guestCart
    .filter((item) => item.sizeVariantId && item.colorVariantId)
    .map(toGuestAddItemToCartDto);
}
